import { useEffect, useRef, useState } from "react";
import {
  ArrowLeftRight,
  Info,
  LogOut,
  MoreVertical,
  School,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import {
  ContextSwitchModal,
  portalForPath,
  resolvePortals,
  useAuth,
} from "@/features/auth";
import { usePortalLogout } from "../hooks/usePortalLogout";

/** Shared portal actions retain confirmation and keyboard/outside-click dismissal. */
export function AppMenu() {
  const { generation } = useAuth();
  // A different school, account or login must never retain an open menu or its previous choices.
  return <ScopedAppMenu key={generation} />;
}

function ScopedAppMenu() {
  const [view, setView] = useState<"actions" | "school" | "portal" | null>(
    null,
  );
  const open = view === "actions";
  const { user, activeTenant, roles } = useAuth();
  const location = useLocation();
  const portals = resolvePortals(roles, activeTenant?.adminTitle);
  const current = portals.find(
    (portal) => portal.role === portalForPath(location.pathname)?.role,
  );
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const logout = usePortalLogout();
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setView(null);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setView(null);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", key);
    container.current?.querySelector<HTMLElement>("nav button, nav a")?.focus();
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", key);
    };
  }, [open]);
  const openPicker = (kind: "school" | "portal") => {
    // The menu item unmounts; give the modal a stable trigger to restore focus to on dismissal.
    trigger.current?.focus();
    setView(kind);
  };
  return (
    <div ref={container} className="relative" dir="rtl">
      <button
        ref={trigger}
        type="button"
        aria-label="گزینه‌های برنامه"
        aria-expanded={open}
        aria-controls="app-actions"
        onClick={() => setView(open ? null : "actions")}
        className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200/90 bg-white text-slate-600 shadow-xs hover:bg-slate-50"
      >
        <MoreVertical className="size-4" aria-hidden="true" />
      </button>
      {open && (
        <nav
          id="app-actions"
          aria-label="گزینه‌های برنامه"
          className="absolute end-0 top-11 z-50 w-60 max-w-[calc(100vw-2rem)] space-y-1 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg"
        >
          {user && user.tenants.length > 1 && (
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={() => openPicker("school")}
              className="focus-visible:outline-brand flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-start hover:bg-violet-50 focus-visible:outline-2"
            >
              <School
                className="size-4 shrink-0 text-violet-600"
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm">تغییر مدرسه فعال</span>
                <span className="mt-0.5 block truncate text-[10px] text-slate-500">
                  {activeTenant?.tenantName}
                </span>
              </span>
            </button>
          )}
          {portals.length > 1 && (
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={() => openPicker("portal")}
              className="focus-visible:outline-brand flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-start hover:bg-violet-50 focus-visible:outline-2"
            >
              <ArrowLeftRight
                className="size-4 shrink-0 text-violet-600"
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm">تغییر پرتال</span>
                {current && (
                  <span className="mt-0.5 block truncate text-[10px] text-slate-500">
                    {current.title}
                  </span>
                )}
              </span>
            </button>
          )}
          <Link
            to="/about"
            onClick={() => setView(null)}
            className="flex min-h-11 items-center gap-3 rounded-xl px-3 py-2 text-sm hover:bg-slate-50"
          >
            <Info className="size-4" aria-hidden="true" />
            درباره برنامه
          </Link>
          <button
            type="button"
            onClick={() => {
              setView(null);
              if (window.confirm("آیا می‌خواهید از حساب کاربری خود خارج شوید؟"))
                logout();
            }}
            className="text-destructive flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-sm hover:bg-red-50"
          >
            <LogOut className="size-4" aria-hidden="true" />
            خروج از حساب
          </button>
        </nav>
      )}
      {(view === "school" || view === "portal") && (
        <ContextSwitchModal kind={view} onDismiss={() => setView(null)} />
      )}
    </div>
  );
}
